import React, { useRef, useState } from "react"
import toast from "react-hot-toast"
import { PaginationState } from "types"
import * as XLSX from "xlsx"
import {
  BaseTable,
  Button,
  Card,
  FooterMenu,
  HeaderMenu,
  Input,
  PaginationData,
  Select
} from "../../../../components"
import { default_pagination_value, useApi } from "../../../../utils"
import { ModalPreview, ProductImportList } from "./components"
import { ModalForm } from "./components/ModalForm"
import { ProductPreviewType } from "./types"
import {
  exportExcel,
  useExportPdf,
  useDropdownFileName,
  useList
} from "./utils"

export default function ProductImport() {
  const api = useApi()
  const [pagination, setPagination] = useState<
    PaginationState & { file_name?: string; file_status?: number }
  >({ ...default_pagination_value, file_name: "", file_status: 1 })
  const { data, isLoading, refetch } = useList(pagination)

  const dataDropdownFilename = useDropdownFileName()
  const dataFile = [
    { label: "ALL", value: "" },
    ...(dataDropdownFilename?.data?.map((val: { file_name: string }) => ({
      label: val.file_name,
      value: val.file_name
    })) || [])
  ]
  const exportPdf = useExportPdf({ data: data?.listfile })
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [modalConfig, setModalConfig] = useState<ModalConfig>({
    type: "",
    data: null,
    show: false,
    identity: ""
  })

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (file) {
      const reader = new FileReader()
      setSelectedFile(file)

      reader.onload = (e) => {
        const binaryStr = e.target?.result as string
        const workbook = XLSX.read(binaryStr, { type: "binary" })
        const sheetName = workbook.SheetNames[0]
        const sheet = workbook.Sheets[sheetName]
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 })

        const headers = jsonData[1] as string[]
        const hasProductBuy = headers.includes("ACC_BUY_NAME")
        const hasProductInv = headers.includes("NO_ACC_INVENTORY")
        const hasProductSell = headers.includes("NO_ACC_SELL")

        if (!hasProductBuy || !hasProductInv || !hasProductSell) {
          toast.error("File format is not correct!")
          handleFileCancel()
          return
        }
      }
      reader.readAsBinaryString(file)
    }
  }
  const handleFileDownload = async () => {
    try {
      const response = await fetch("/format_import_product_final.xlsx")
      if (!response.ok) {
        throw new Error("File not found")
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement("a")
      link.href = url
      link.download = "product_import.xlsx"
      link.click()

      window.URL.revokeObjectURL(url)
    } catch (error) {
      alert("File tidak tersedia!")
    }
  }
  const handleFileCancel = () => {
    setSelectedFile(null)
    setModalConfig({ ...modalConfig, type: "", show: false })

    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleSaveFileExcel = () => {
    if (!selectedFile) {
      toast.error("File not selected!")
      return
    }

    const formData = new FormData()
    formData.append("ExcelFile", selectedFile)

    return new Promise<void>((resolve) => {
      toast
        .promise(
          api.post("/importexcel/importexcel", formData, {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          }),
          {
            loading: "Loading...",
            success: (res) => res.data.message,
            error: (err) => err.response.data.message ?? err.response.message
          }
        )
        .then((res) =>
          setModalConfig({
            ...modalConfig,
            type: "preview",
            show: true,
            data: res?.data?.payload?.data,
            identity: res?.data?.payload?.identity_batch
          })
        )
        .catch(() => setSelectedFile(null))
        .finally(resolve)
    })
  }

  /* eslint-disable @typescript-eslint/no-use-before-define */
  const dropdownStatus = [
    { label: "REVIEW", value: StatusEnum.Review?.toString() },
    { label: "SUBMIT", value: StatusEnum.Submit?.toString() }
  ]

  return (
    <section className="container pt-5  gap-5 flex flex-col">
      <HeaderMenu title="PRODUCT IMPORT" />

      <Card>
        {/* IMPORT SECTION */}
        <Card.Body className="flex-row justify-between items-center">
          <div className="flex items-center gap-3">
            <Input
              className="w-[300px] cursor-pointer"
              placeholder="Choose file"
              value={selectedFile?.name ? selectedFile?.name : ""}
            />
            <Input
              type="file"
              ref={fileInputRef}
              className="w-[300px] absolute opacity-0 cursor-pointer"
              accept=".xlsx, .xls"
              onChange={handleFileChange}
            />
            <Button
              color="primary"
              onClick={handleSaveFileExcel}
              disabled={!selectedFile}>
              SAVE
            </Button>
          </div>

          <Button color="primary" onClick={handleFileDownload}>
            FORMAT DOWNLOAD
          </Button>
        </Card.Body>

        {/* FILTER SECTION */}
        <Card.Body className="flex-row justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="text-nowrap">
                <b>FILE NAME</b>
              </div>
              <div className="w-[200px]">
                <Select
                  options={dataFile}
                  value={dataFile?.find(
                    (item) => item.value === pagination?.file_name
                  )}
                  onChange={(item) =>
                    setPagination((prev) => {
                      return { ...prev, file_name: item?.value }
                    })
                  }
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-nowrap">
                <b>STATUS</b>
              </div>
              <div className="w-[200px]">
                <Select
                  options={dropdownStatus}
                  value={dropdownStatus.find(
                    (item) =>
                      item.value?.toString() ===
                      pagination?.file_status?.toString()
                  )}
                  onChange={(item) =>
                    setPagination((prev) => {
                      return { ...prev, file_status: Number(item?.value) }
                    })
                  }
                />
              </div>
            </div>
          </div>
        </Card.Body>

        <Card.Body>
          <BaseTable>
            <thead>
              <tr>
                <th>NO</th>
                <th>DATE</th>
                <th>FILE NAME</th>
                <th>DESCRIPTION</th>
                <th>STATUS</th>
                <th className="w-[150px]">ACTION</th>
              </tr>
            </thead>
            <tbody>
              <ProductImportList
                data={data?.listfile}
                isLoading={isLoading}
                refetch={refetch}
                currentPage={pagination?.page}
                limit={pagination?.limit}
                size={data?.pagination_data?.size}
                total={pagination?.total}
                modalConfig={modalConfig}
                setModalConfig={setModalConfig}
              />
            </tbody>
          </BaseTable>
          <div className="mt-5">
            <PaginationData
              data={data}
              pagination={pagination}
              setPagination={setPagination}
            />
          </div>
        </Card.Body>
      </Card>

      <FooterMenu
        export={{
          actiontype: "export",
          onClick: () => exportExcel({ data: data?.listfile })
        }}
        pdf={{
          actiontype: "pdf",
          onClick: () => exportPdf.handleExportPdf()
        }}
      />

      {/* MODAL PREVIEV FILE XLSX */}
      <ModalPreview
        data={modalConfig?.data || []}
        show={modalConfig?.type === "preview" && modalConfig?.show}
        handleCloseModal={handleFileCancel}
        handleSubmitModal={() =>
          setModalConfig({ ...modalConfig, type: "form", show: true })
        }
      />

      {/* MODAL INPUT FORM */}
      <ModalForm
        identity={modalConfig?.identity || ""}
        show={modalConfig?.type === "form" && modalConfig?.show}
        onClose={handleFileCancel}
        refetch={refetch}
      />
    </section>
  )
}

type ModalConfig = {
  identity?: string
  type: string
  data: ProductPreviewType[] | null
  show: boolean
}
export enum StatusEnum {
  Review = 1,
  Submit = 2
}
